// src/lib/analytics/ga-client-id.ts
/**
 * @fileoverview Shared, client-safe GA4 `client_id` parsing.
 * @description The single source of truth for reading the GA `client_id` out of
 * the browser's `_ga` cookie. Lives in its own module (NO `~/env` import, so it's
 * safe in the client bundle) and is used by BOTH:
 *  - `BuyButton` (client) — captures the id to thread into Stripe Checkout, and
 *  - `~/server/analytics/measurement-protocol` (server) — re-exports it for the
 *    webhook's server-side `purchase`.
 *
 * Keeping one regex here means the `_ga` cookie-format contract can't drift
 * between the client capture and the server fallback (a past defect: two copies).
 */

/**
 * Parse the GA `client_id` (`XXXXXXXXX.YYYYYYYYYY`) out of a raw `_ga` cookie
 * VALUE (`GA1.1.XXXXXXXXX.YYYYYYYYYY`). The version and domain-depth components
 * allow **multiple digits** (`GA1.10.…` on deeply-nested subdomains is valid).
 * Returns `undefined` when the shape doesn't match.
 */
export function parseGaClientId(rawGaCookie: string | undefined | null): string | undefined {
  if (!rawGaCookie) return undefined
  // _ga = GA<version>.<domainDepth>.<clientId1>.<clientId2>
  const m = /^GA\d+\.\d+\.(\d+\.\d+)$/.exec(rawGaCookie.trim())
  return m?.[1]
}

/**
 * Read + parse the GA `client_id` from a `document.cookie` string. Returns
 * `undefined` when the `_ga` cookie is absent (GA not loaded / blocked).
 */
export function gaClientIdFromCookie(cookieString: string | undefined): string | undefined {
  if (!cookieString) return undefined
  const match = /(?:^|;\s*)_ga=([^;]+)/.exec(cookieString)
  if (!match?.[1]) return undefined
  // A corrupt `_ga` value (invalid percent-escape from an extension or a manual
  // cookie edit) makes `decodeURIComponent` THROW. This runs in `BuyButton`
  // before the `/api/checkout` POST, whose outer catch would then surface the
  // generic checkout error and BLOCK the purchase — optional analytics stitching
  // must never do that. Treat a decode failure as "no client id" and continue.
  let decoded: string
  try {
    decoded = decodeURIComponent(match[1])
  } catch {
    return undefined
  }
  return parseGaClientId(decoded)
}

/** GA4 session id + number parsed from the per-stream `_ga_<container>` cookie. */
export interface GaSession {
  /** GA4 session id (the session-start unix timestamp; digits only). */
  sessionId: string
  /** Running per-user session count, when present. */
  sessionNumber?: string
}

/**
 * Derive the GA4 SESSION cookie name (`_ga_<container>`) from the measurement id:
 * `G-ABC123` → `_ga_ABC123` (the id minus the `G-` prefix). This per-stream
 * cookie — distinct from the client-id `_ga` cookie — holds the session id +
 * number that the server-side Measurement Protocol needs for session attribution.
 */
export function gaSessionCookieName(measurementId: string | undefined | null): string | undefined {
  if (!measurementId) return undefined
  const suffix = measurementId.trim().replace(/^G-/, "")
  return suffix ? `_ga_${suffix}` : undefined
}

/**
 * Parse the GA4 session id (+ number) out of a `_ga_<container>` cookie VALUE,
 * handling BOTH formats Google ships during its 2025 cookie migration:
 *  - **GS1** (positional): `GS1.1.<sessionId>.<sessionNumber>.<engaged>.<…>`
 *  - **GS2** (key-value):  `GS2.1.s<sessionId>$o<sessionNumber>$g<…>$t<…>…`
 *    (`$`-delimited, `s`=session id, `o`=session number).
 * `sessionId` is GA4's session-start timestamp (digits). Returns `undefined`
 * when neither shape matches.
 */
export function parseGaSessionId(rawCookie: string | undefined | null): GaSession | undefined {
  if (!rawCookie) return undefined
  const v = rawCookie.trim()
  // GS2 (newer): dollar-delimited key-value after the `GS2.<v>.` prefix.
  const gs2 = /^GS2\.\d+\.(.+)$/.exec(v)
  if (gs2?.[1]) {
    let sessionId: string | undefined
    let sessionNumber: string | undefined
    for (const tok of gs2[1].split("$")) {
      if (tok.startsWith("s")) sessionId = tok.slice(1)
      else if (tok.startsWith("o")) sessionNumber = tok.slice(1)
    }
    if (sessionId && /^\d+$/.test(sessionId)) {
      return {
        sessionId,
        sessionNumber: sessionNumber && /^\d+$/.test(sessionNumber) ? sessionNumber : undefined,
      }
    }
    return undefined
  }
  // GS1 (legacy): positional, dot-delimited.
  const gs1 = /^GS1\.\d+\.(\d+)\.(\d+)/.exec(v)
  if (gs1?.[1]) return { sessionId: gs1[1], sessionNumber: gs1[2] }
  return undefined
}

/**
 * Read + parse the GA4 session id from a `document.cookie` string for the given
 * measurement id. Returns `undefined` when the session cookie is absent/blocked
 * or malformed. Never throws (a corrupt percent-escape degrades to `undefined`)
 * — like {@link gaClientIdFromCookie}, this runs before the checkout POST and
 * must never block a purchase.
 */
export function gaSessionFromCookie(
  cookieString: string | undefined,
  measurementId: string | undefined | null,
): GaSession | undefined {
  if (!cookieString) return undefined
  const name = gaSessionCookieName(measurementId)
  if (!name) return undefined
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  const match = new RegExp(`(?:^|;\\s*)${escaped}=([^;]+)`).exec(cookieString)
  if (!match?.[1]) return undefined
  let decoded: string
  try {
    decoded = decodeURIComponent(match[1])
  } catch {
    return undefined
  }
  return parseGaSessionId(decoded)
}
